import React, { useEffect, useMemo, useState } from "react";
import { ChevronDown } from "lucide-react";
import NoRecordsAvailable from "../../components/NoRecordsAvailable";
import approvalStatusOptions from "../../options/approvalStatusOptions";
import {
  Accordion,
  AccordionContent,
  AccordionHeader,
  AccordionItem,
  AccordionTrigger,
  Badge,
  Modal,
  ResponsiveDataTable,
} from "../../v2/components/ui/index.ts";

import modalStyles from "./wfh-timesheet-view-modal.module.css";

function formatDurationMinutes(duration) {
  const minutes = Number(duration) || 0;
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return `${hours}h : ${remainingMinutes}m`;
}

function formatTaskStatus(status) {
  if (status === "COMPLETED") return "Completed";
  if (status === "INPROGRESS") return "In-Progress";
  return status ?? "-";
}

function formatEntryTitle(title) {
  const normalized = String(title ?? "")
    .trim()
    .replace(/[-_]+/g, " ")
    .replace(/\s*&\s*/g, " & ")
    .replace(/\s+/g, " ");

  if (!normalized) return "-";

  return normalized
    .split(" ")
    .map((word) => {
      if (word === "&") return word;
      if (word.length === 1) return word.toUpperCase();
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(" ");
}

/** Roll up entry-level approvalStatus for the accordion group header. */
function getGroupApprovalSummary(entries) {
  const statuses = (entries ?? [])
    .map((e) => e.approvalStatus)
    .filter((s) => s != null && s !== "");
  if (statuses.length === 0) {
    return { variant: "neutral", label: "-" };
  }
  if (statuses.every((s) => s === "APPROVED")) {
    const opt = approvalStatusOptions.find((o) => o.value === "APPROVED");
    return { variant: "success", label: opt?.label ?? "Approved" };
  }
  if (statuses.some((s) => s === "REJECTED")) {
    const opt = approvalStatusOptions.find((o) => o.value === "REJECTED");
    return { variant: "danger", label: opt?.label ?? "Rejected" };
  }
  const opt = approvalStatusOptions.find((o) => o.value === "PENDING");
  return { variant: "warning", label: opt?.label ?? "Pending" };
}

function formatApproverLabel(approval) {
  const name = approval?.approverName;
  if (name != null && String(name).trim() !== "") return String(name).trim();
  const t = approval?.approverType;
  if (t === "PM") return "Project Manager";
  if (t === "OTHERS" || t === "OTHER") return "Other";
  return t != null && String(t).trim() !== "" ? String(t) : "—";
}

function formatApproverTypeOnly(t) {
  if (t == null || String(t).trim() === "") return "—";
  if (t === "PM") return "Project Manager";
  if (t === "OTHERS" || t === "OTHER") return "Other";
  return String(t);
}

/** Approver line next to project / miscellaneous title, from entries + sheet approvals map. */
function getGroupApproverDisplay(entries, idToLabel) {
  const ids = [
    ...new Set(
      (entries ?? [])
        .map((e) => e.approverId)
        .filter((id) => id != null && id !== ""),
    ),
  ];
  const fromIds = ids
    .map((id) => idToLabel.get(Number(id)))
    .filter((s) => s != null && String(s).trim() !== "" && s !== "—");
  const unique = [...new Set(fromIds)];
  if (unique.length > 0) return unique.join(", ");

  const types = [
    ...new Set(
      (entries ?? [])
        .map((e) => e.approverType)
        .filter((t) => t != null && String(t).trim() !== ""),
    ),
  ];
  if (types.length === 0) return "—";
  return [...new Set(types.map(formatApproverTypeOnly))].join(", ");
}

function groupTimesheetEntries(entries) {
  if (!entries?.length) return [];
  const groups = new Map();

  for (const entry of entries) {
    if (entry.titleType === "OTHERS") {
      const key = "others";
      if (!groups.has(key)) {
        groups.set(key, { key, label: "Miscallaneous", entries: [] });
      }
      groups.get(key).entries.push(entry);
      continue;
    }

    const key =
      entry.projectId != null
        ? `project-${entry.projectId}`
        : `project-${String(entry.projectName || "unnamed").replace(/\s+/g, "-")}`;

    if (!groups.has(key)) {
      groups.set(key, {
        key,
        label: entry.projectName || "Project",
        entries: [],
      });
    }
    groups.get(key).entries.push(entry);
  }

  return Array.from(groups.values());
}

const WFHTimesheetManagementViewModal = ({
  open,
  onClose,
  timesheet,
}) => {
  const [accordionOpen, setAccordionOpen] = useState(null);

  const approverDisplayById = useMemo(() => {
    const map = new Map();
    for (const a of timesheet?.approvals ?? []) {
      if (a?.approverId != null) {
        map.set(Number(a.approverId), formatApproverLabel(a));
      }
    }
    return map;
  }, [timesheet?.approvals]);

  const projectGroups = useMemo(
    () => groupTimesheetEntries(timesheet?.entries ?? []),
    [timesheet?.entries],
  );
  const totalDurationMinutes =
    timesheet?.totalDurationMinutes ??
    (timesheet?.entries ?? []).reduce(
      (total, entry) => total + (Number(entry.duration) || 0),
      0,
    );

  const tableColumns = useMemo(
    () => [
      {
        id: "category",
        header: "Category",
        accessor: "category",
        sortable: true,
        truncate: true,
        getTitleText: (row) => String(row.category ?? ""),
      },
      {
        id: "description",
        header: "Task Description",
        accessor: "description",
        sortable: true,
        // truncate: true,
        getTitleText: (row) => String(row.description ?? ""),
      },
      {
        id: "taskStatus",
        header: "Task Status",
        accessor: "taskStatus",
        sortable: true,
      },
      {
        id: "timeLabel",
        header: "Time",
        accessor: "timeLabel",
        sortable: true,
        nowrap: true,
      },
    ],
    [],
  );

  useEffect(() => {
    if (!open) return;
    setAccordionOpen(null);
  }, [open, timesheet?.id]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Timesheet Details"
      size="lg"
    >
      <div className={modalStyles.wrap}>
        <div className={modalStyles.summaryRow}>
          <p className={modalStyles.projectCount}>
            {projectGroups.length}{" "}
            {projectGroups.length === 1 ? "project" : "projects"}
          </p>
          <p className={modalStyles.projectCount}>
            Total Time: <strong>{formatDurationMinutes(totalDurationMinutes)}</strong>
          </p>
        </div>

        {projectGroups.length === 0 ? (
          <NoRecordsAvailable />
        ) : (
          <Accordion
            type="single"
            value={accordionOpen}
            onValueChange={setAccordionOpen}
            collapsible
            className={modalStyles.accordion}
          >
            {projectGroups.map((group) => {
              const groupSummary = getGroupApprovalSummary(group.entries);
              const groupApproverText =
                group.key === "others"
                  ? (timesheet?.approvals?.find((a) => a.projectId == null || a.approverType === "RM")?.approverName || "—")
                  : getGroupApproverDisplay(
                      group.entries,
                      approverDisplayById,
                    );

              const rows = group.entries.map((entry, idx) => ({
                id: `${group.key}-${idx}`,
                category: formatEntryTitle(entry.titleName),
                description: entry.description || "-",
                taskStatus: formatTaskStatus(entry.taskStatus),
                timeLabel: formatDurationMinutes(entry.duration),
              }));

              return (
                <AccordionItem
                  key={group.key}
                  value={group.key}
                  className={modalStyles.accordionItemCard}
                >
                  <AccordionHeader
                    className={`${modalStyles.itemHeader} ${modalStyles.itemHeaderRich}`}
                  >
                    <div className={modalStyles.headerBlock}>
                      <span className={`sub-heading-md ${modalStyles.projectTitle}`}>
                        {group.label}
                      </span>
                      <div className={modalStyles.headerMeta}>
                        <span className={modalStyles.metaLabel}>Approver</span>
                        <span
                          className={modalStyles.groupApprover}
                          title={groupApproverText}
                        >
                          {groupApproverText}
                        </span>
                        <span className={modalStyles.metaSep} aria-hidden>
                          ·
                        </span>
                        <Badge variant={groupSummary.variant}>
                          {groupSummary.label}
                        </Badge>
                      </div>
                    </div>
                    <AccordionTrigger className={modalStyles.iconTrigger}>
                      <ChevronDown
                        className={modalStyles.chevron}
                        size={20}
                        strokeWidth={2}
                        aria-hidden
                      />
                    </AccordionTrigger>
                  </AccordionHeader>
                  <AccordionContent className={modalStyles.accordionContent}>
                    <ResponsiveDataTable
                      pagination={false}
                      columns={tableColumns}
                      data={rows}
                      aria-label={`Tasks for ${group.label}`}
                      emptyState={
                        <span
                          className="body-sm"
                          style={{ color: "var(--content-muted)" }}
                        >
                          No rows
                        </span>
                      }
                    />
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>
        )}
      </div>
    </Modal>
  );
};

export default WFHTimesheetManagementViewModal;
